import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useTheme
} from "@mui/material";
import AsteriskSolid from "../../../assets/faIcons/AsteriskSolid";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiSVGIcon from "../../IconPicker/MuiSVGIcon";


//TODO - Select needs a "Select All". Currently the only instance of a select all we need is with
// batch assets. But since no one is using batch's this is not important. Below is a snippet from
// the old select all code. It is not complete and will not work. But it is a good starting point

// Another function the old react-select search had was the ability to search for items. We need to figure this out with MUI
// Looks like we have MUI autocomplete. We even have a component for this. We need to style the
// Component to look like this one at some point. But for now, we can use this one.

// const handleItemSelectChange = (event, actionMeta) => {
//   const { option = {}, action = "" } = actionMeta;
//   const { value = "" } = option;

//   if (
//     updateEvent.assets?.some((item) => item.value === "<SELECT_ALL>") &&
//     action === "deselect-option"
//   ) {
//     // If select all is enabled and user then de-selects an item, we remove select all from the selected items
//     setUpdateEvent((prevState) => ({
//       ...prevState,
//       assets: [...event?.filter((item) => item.value !== "<SELECT_ALL>")],
//     }));
//   } else if (
//     value === "<SELECT_ALL>" &&
//     updateEvent.assets?.some((item) => item.value === "<SELECT_ALL>")
//   ) {
//     // If user has select all enabled, and then clicks it again. We deselect all assets
//     setUpdateEvent((prevState) => ({ ...prevState, assets: [] }));
//   } else if (value === "<SELECT_ALL>") {
//     // If user clicks select all, we add all assets
//     setUpdateEvent((prevState) => ({
//       ...prevState,
//       assets: itemOptions,
//     }));
//   } else {
//     setUpdateEvent((prevState) => ({ ...prevState, assets: event }));
//   }
// };

function SimpleSelect(props) {
  const {
    disabled,
    error,
    errorText = "Required Field",
    helperText,
    id,
    inputProps,
    label,
    labelPlacement = "top",
    labelStyles = {},
    margin,
    MenuProps: menuPropsOverride,
    multiple = false,
    onChange,
    onClear,
    onChipDelete = () => { },
    onDeleteIconClick = () => { },
    options,

    // If present a placeholder will be added as an option with the value of ""
    placeHolderText,
    readOnly,
    required,
    showDeleteIcon = false,
    size = "small",
    style,
    type,
    value = multiple ? [] : "",
    variant,
  } = props;
  const theme = useTheme()
  const { palette = {} } = theme
  const { typography = {}, success = {}, background = {}, text = {}, divider, mode } = palette
  const isDark = mode === "dark";
  const formattedLabel = label?.replace(/\s+/g, "-");
  
  // Brand colors
  const greenColor = success?.main || "#A0C46D";
  const greenDark = "#7a9c4a";
  const greenLight = isDark ? "rgba(160, 196, 109, 0.12)" : "rgba(160, 196, 109, 0.08)";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.3)" : "#e2e8f0");

  let labelWithAstrisk;
  if (required) {
    labelWithAstrisk = (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <MuiSVGIcon
          icon={<AsteriskSolid />}
          sx={{
            color: isDark ? "#f87171" : "#ef4444",
            fontSize: "8px",
          }}
        />
      </Box>
    );
  }

  const isStandard = variant === "standard";

  // Select styling — adapts to outlined vs standard variant
  const selectSx = {
    ...(!isStandard && {
      borderRadius: "8px",
      backgroundColor: bgPaper,
    }),
    transition: "all 0.2s ease-in-out",

    // Outlined variant
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
      transition: "all 0.2s ease-in-out",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: greenColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: greenColor,
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: isStandard ? "none" : `0 0 0 3px rgba(160, 196, 109, ${isDark ? '0.15' : '0.1'})`,
    },
    "&.Mui-disabled": {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.05)" : "#f8fafc",
    },

    // Standard variant underline
    ...(isStandard && {
      backgroundColor: "transparent !important",
      "&.MuiInput-root": {
        backgroundColor: "transparent !important",
      },
      "&.MuiInputBase-root": {
        backgroundColor: "transparent !important",
      },
      "&:before": {
        borderBottomColor: borderColor,
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: greenColor,
      },
      "&:after": {
        borderBottomColor: greenColor,
      },
    }),

    "& .MuiSelect-select": {
      color: textPrimary,
      fontSize: "0.875rem",
      ...(isStandard && {
        paddingBottom: "6px",
        backgroundColor: "transparent !important",
        "&:focus": {
          backgroundColor: "transparent !important",
        },
      }),
    },
    "& .MuiSelect-icon": {
      color: textSecondary,
      transition: "transform 0.2s ease-in-out",
    },
    "&.Mui-focused .MuiSelect-icon": {
      color: greenColor,
    },
  };

  // Menu styling (menuPropsOverride can override e.g. disablePortal for scrollable parents)
  const menuProps = {
    disablePortal: true,
    ...menuPropsOverride,
    PaperProps: {
      sx: {
        borderRadius: "12px",
        boxShadow: isDark ? "0 10px 40px rgba(0, 0, 0, 0.4)" : "0 10px 40px rgba(0, 0, 0, 0.12)",
        border: `1px solid ${borderColor}`,
        marginTop: "4px",
        backgroundColor: bgPaper,
        "& .MuiMenuItem-root": {
          padding: "10px 16px",
          fontSize: "0.875rem",
          color: textPrimary,
          transition: "all 0.15s ease-in-out",
          "&:hover": {
            backgroundColor: greenLight,
            color: greenDark,
          },
          "&.Mui-selected": {
            backgroundColor: isDark ? "rgba(160, 196, 109, 0.2)" : "rgba(160, 196, 109, 0.15)",
            color: greenDark,
            fontWeight: 500,
            "&:hover": {
              backgroundColor: isDark ? "rgba(160, 196, 109, 0.25)" : "rgba(160, 196, 109, 0.2)",
            },
          },
          "&.Mui-disabled": {
            color: textSecondary,
            opacity: 0.7,
          },
        },
      },
    },
  };

  return (
    <Box
      sx={
        labelPlacement === "start"
          ? {
            display: "flex",
            alignItems: "center",
          }
          : {}
      }
    >
      {label ? (
        <Box
          sx={{
            color: textSecondary,
            display: "flex",
            fontFamily: "Lato, sans-serif",
            fontWeight: "600",
            justifyContent: "space-between",
            lineHeight: 1,
            marginBottom: `${labelPlacement === "start" ? "inherit" : "8px"}`,
            marginTop: `${labelPlacement === "start" ? "inherit" : "4px"}`,
            marginRight: `${labelPlacement === "start" ? "12px" : ""}`,
            ...labelStyles,
          }}
        >
          <InputLabel
            id={formattedLabel}
            shrink={props.shrink}
            sx={{
              color: textSecondary,
              fontFamily: "Lato, sans-serif",
              fontWeight: 600,
              fontSize: "0.875rem",
              position: "relative",
              transform: "none",
            }}
          >
            {required ? labelWithAstrisk : label ? label : "Select"}
          </InputLabel>
          {showDeleteIcon ? (
            <IconButton
              aria-label={`Delete ${label}`}
              onClick={() => onDeleteIconClick()}
              sx={{
                padding: "4px",
                color: "#94a3b8",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  color: "#ef4444",
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" cursor="pointer" />
            </IconButton>
          ) : null}
          {onClear ? (
            <Typography
              className={value?.length > 0 ? "pointer" : ""}
              onClick={onClear}
              //if user hits enter or space, invoke the onClear function
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onClear();
                }
              }}
              role="button"
              sx={{
                cursor: value.length > 0 ? "pointer" : "not-allowed",
                textDecoration: "underline",
                color: value.length > 0 ? greenDark : "#94a3b8",
                fontSize: "0.8125rem",
                fontWeight: 500,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  color: value.length > 0 ? greenColor : "#94a3b8",
                },
              }}
              tabIndex={0}
              variant="caption"
            >
              Clear
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <FormControl
        disabled={readOnly}
        className="simple-select"
        error={error}
        size={size}
        sx={{ width: "100%" }}
        variant={variant ? variant : "outlined"}
      >
        <Select
          disabled={disabled}
          id={id}
          inputProps={{ readOnly: readOnly, ...inputProps }}
          data-cypress-id={inputProps?.["data-cypress-id"]}
          displayEmpty={placeHolderText ? true : false}
          labelId={formattedLabel}
          margin={margin}
          MenuProps={menuProps}
          multiple={multiple}
          onChange={(e) => {
            onChange(e);
          }}
          renderValue={
            multiple
              ? (selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      deleteIcon={
                        <CancelIcon
                          // Stops the select menu from activating when they click on the cancel icon
                          onMouseDown={(event) => event.stopPropagation()}
                          sx={{
                            color: "#64748b !important",
                            fontSize: "1rem !important",
                            "&:hover": {
                              color: "#ef4444 !important",
                            },
                          }}
                        />
                      }
                      key={value}
                      // Label is stolen from https://stackoverflow.com/questions/73998290/material-ui-how-to-rendervalue-display-label-of-select-tag
                      // The lack of control with this prop is funny
                      label={
                        options.find((item) => item.value === value)?.label
                      }
                      onDelete={() => onChipDelete(value)}
                      size="small"
                      sx={{
                        borderRadius: "6px",
                        backgroundColor: greenLight,
                        color: greenDark,
                        fontWeight: 500,
                        fontSize: "0.8125rem",
                        border: `1px solid ${greenColor}`,
                        "& .MuiChip-label": {
                          padding: "0 8px",
                        },
                      }}
                    />
                  ))}
                </Box>
              )
              : null
          }
          required={required}
          style={style}
          type={type}
          value={value}
          sx={selectSx}
        >
          {placeHolderText ? (
            <MenuItem disabled value="">
              <em style={{ color: "#94a3b8", fontStyle: "normal" }}>{placeHolderText}</em>
            </MenuItem>
          ) : null}
          {options && options.length ? (
            options.map((option, idx) => (
              <MenuItem
                disabled={option.disabled}
                value={option.value}
                key={`${idx} - ${option.value}`}
              >
                {option.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <em style={{ color: "#94a3b8" }}>None</em>
            </MenuItem>
          )}
        </Select>
        {error ? (
          <FormHelperText sx={{ marginLeft: "4px", marginTop: "6px", fontSize: "0.75rem" }}>
            {errorText}
          </FormHelperText>
        ) : null}
        {helperText ? (
          <FormHelperText sx={{ marginLeft: "4px", marginTop: "6px", fontSize: "0.75rem", color: "#64748b" }}>
            {helperText}
          </FormHelperText>
        ) : null}
      </FormControl>
    </Box>
  );
}

export default SimpleSelect;
